import React, { useEffect, useState, useContext, useCallback, useMemo, useRef, lazy } from 'react'
import { Helmet } from 'react-helmet'
import searchMetadata from '@/data/searchMetadata.json'
import { useSearchParams, useLocation, useParams } from 'react-router-dom'
import './ProductPage.css'
import { IProductImage, ActiveAddonItem, AddonInputValue } from '@/types'
import ProductInfo from '@/components/ProductInfo'
import Skeleton from '@/components/Skeleton'
import { IAddonItem } from '@/components/Addon'
import { trackViewContent, trackStartCustomization, trackCompleteCustomization, trackAddToCart } from '@/utils/tracking'
import useRegionStore from '@/context/RegionStore'
import { useCurrency } from '@/hooks/useCurrency'
import { formatPriceWithSymbol } from '@/utils/priceFormatting'
import { capDisplayRating } from '@/utils/ratingDisplay'
import { fetchReviewSummary, ReviewSummary } from '@/services/reviewsApi'
import { format } from 'date-fns'
import SelectedItems from '@/components/SelectedItems'

import { productsData } from '@/data/productData'
import useProductDataStore, { IOption, IPartsModifications, IProduct, StockStatus } from '@/context/ProductDataStore'
import { fetchProductData, fetchStockCheck, StockCheckCalculatedStock, StockCheckItem, StockCheckResponse } from '@/services/api'
import { mergeCalculatedStock, isPurchasableStatus, buildStockMapWithSharedPoolPreorder } from '@/utils/stockUtils'
import useBasketStore, { IBasketItem } from '@/context/BasketStore'
import { uuidv4 } from '@/services/helpers'
import LoadingRing from '@/components/LoadingRing'
import InlineReviews from '@/components/InlineReviews'
import ProductImageSection from '@/components/ProductImageSection'
import ProductAddonSelector from '@/components/ProductAddonSelector'
import ProductActionButton from '@/components/ProductActionButton'
import ProductModals from '@/components/ProductModals'
import MadeInHackney from '@/components/MadeInHackney'
import { ButtonVariant, getVariantClasses } from '@/components/Button'
import { galleryImages } from '@/modules/scrolling-gallery/data/galleryExamples'
import { ScrollContainerContext } from '@/components/ScrollToTop'
import useCustomColoursStore from '@/context/CustomColoursStore'
import useCountryDetailsStore from '@/context/CountryDetailsStore'
import useDiscountStore from '@/context/DiscountStore'
import useDiscountCapture from '@/hooks/useDiscountCapture'
import { parseVariantFromUrl, cleanUrlParams } from '@/utils/productUrls'
import { getRegionFromUrl } from '@/utils/regionDetection'
import { getRegionalProductPath, SEO_REGIONS, toAbsoluteSiteUrl } from '@/utils/seoUrls'
import { getLegacyThumbnailPath, getProductThumbnailUrl } from '@/utils/productImages'
import useStockNoticeStore from '@/context/StockNoticeStore'
import ProductPageShell from '@/components/product-page/ProductPageShell'
import ProductLoadFailure from '@/components/product-page/ProductLoadFailure'
import { rankColourways, chooseRankedColourway, canSelectColourway, canDisplayColourway, nextColourway, ColourwayAvailability } from '@/utils/colourwaySelection'
const ProductBottomSections = lazy(() => import('@/components/ProductBottomSections'));


interface ICustomComponent {
  componentVariantId: { $oid: string }
  componentType: string
  name: string
  position: string | null
  chosenMaterial: { $oid: string }
  display?: Array<{
    configuratorFrontOn: boolean
    'z-index'?: number
  }>
  materialOptions?: string[]
}

interface ProductPageProps {
  toggleOverlay: Function
  navHeight: number
  setNavVisibilityOverride: (override: boolean | null) => void
}

interface ColorwayTheme {
  isDarkBackground: boolean
  backgroundColor: string
  navBackgroundColor: string
  accentColor: string
  textColor: string
  primaryTextColor: string
  buttonVariant: ButtonVariant
  borderColor: string
  specialLogo?: string
  specialLogoSize?: string
  descriptionOverride?: string
  descriptionLink?: {
    to: string
    text: string
    className?: string
  }
  images: Array<{
    url: string
    showThumbnail: boolean
    alt: string
  }>
}

const normalizeProductImages = (images: unknown, fallbackAlt: string): IProductImage[] => {
  if (!Array.isArray(images)) {
    return []
  }

  const parsed: Array<{
    url: string
    thumbnailUrl?: string
    backgroundUrl?: string
    alt: string
    order?: number
  }> = []

  images.forEach((image) => {
    if (typeof image === 'string') {
      parsed.push({ url: image, alt: fallbackAlt })
      return
    }

    if (image && typeof image === 'object') {
      const record = image as Record<string, unknown>
      const url = record.url || record.imageUrl || record.primary || record.src
      if (typeof url !== 'string') {
        return
      }

      const thumbnail =
        record.thumbnailUrl ||
        record.thumbnail_url ||
        record.thumbnail ||
        record.thumb
      const background = record.backgroundUrl || record.background_url
      const alt = typeof record.alt === 'string' ? record.alt : fallbackAlt
      const order = typeof record.order === 'number' ? record.order : undefined

      parsed.push({
        url,
        thumbnailUrl: typeof thumbnail === 'string' ? thumbnail : undefined,
        backgroundUrl: typeof background === 'string' ? background : undefined,
        alt,
        order,
      })
    }
  })

  return parsed
    .slice()
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    .map((image) => ({
      url: image.url,
      thumbnailUrl: image.thumbnailUrl,
      backgroundUrl: image.backgroundUrl,
      showThumbnail: true,
      alt: image.alt || fallbackAlt,
    }))
}

const normalizeHeroImage = (heroImage: unknown, fallbackAlt: string): IProductImage | null => {
  if (!heroImage || typeof heroImage !== 'object') {
    return null
  }

  const record = heroImage as Record<string, unknown>
  const foreground = record.foregroundUrl || record.foreground_url || record.url
  if (typeof foreground !== 'string') {
    return null
  }

  const background = record.backgroundUrl || record.background_url
  const thumbnail =
    record.thumbnailUrl ||
    record.thumbnail_url ||
    record.thumbnail ||
    record.thumb
  const alt = typeof record.alt === 'string' ? record.alt : fallbackAlt

  return {
    url: foreground,
    backgroundUrl: typeof background === 'string' ? background : undefined,
    thumbnailUrl: typeof thumbnail === 'string' ? thumbnail : undefined,
    showThumbnail: true,
    alt,
  }
}

const ProductPage: React.FC<ProductPageProps> = ({ toggleOverlay, navHeight, setNavVisibilityOverride }) => {
  const [searchParams] = useSearchParams()
  const location = useLocation()
  const urlRegion = getRegionFromUrl(location.pathname)
  const { variant: pathVariant } = useParams()
  const { product, options, materialInformation, setProductData } = useProductDataStore()
  const { setBasketOpen, addItemToBasket, basketItems, updateBasketItem } = useBasketStore()
  const { show: showStockNotice } = useStockNoticeStore()
  const { storeRegion, chosenRegion } = useRegionStore()
  const { symbol: currencySymbol, code: currencyCode, formatPrice, freeDeliveryThreshold, isLoading: isCurrencyLoading } = useCurrency()
  const { countryDetails } = useCountryDetailsStore()
  const contentRef = useContext(ScrollContainerContext);
  const { discountCode, isAddonFree } = useDiscountStore()

  // Capture discount codes from URL - but clean up the URL first
  const cleanedLocation = { ...location, search: cleanUrlParams(location.search) }
  useDiscountCapture()

  const DEFAULT_PRODUCT_ID = "67421f7894b683dbd1ba6491"
  const CUSTOM_COLORS_ID = "674b4a1f197065b8a54a3224"
  const productId = parseVariantFromUrl(pathVariant, searchParams.get('variant'), '')
  const productRequestId = DEFAULT_PRODUCT_ID
  // Review summary fed to the top-of-page rating so it shares one source with the
  // reviews block below (avoids count/rating drift between the 30-min cached
  // product payload and the reviews summary endpoint).
  const [reviewSummary, setReviewSummary] = useState<ReviewSummary | null>(null)
  useEffect(() => {
    let cancelled = false
    const rgn = (chosenRegion || storeRegion || 'gb').toLowerCase()
    fetchReviewSummary(productRequestId, rgn)
      .then((s) => { if (!cancelled) setReviewSummary(s) })
      .catch(() => { /* leave top rating to fall back to product payload */ })
    return () => { cancelled = true }
  }, [productRequestId, chosenRegion, storeRegion])
  const [isLoading, setIsLoading] = useState(true)
  const [productLoadError, setProductLoadError] = useState<string | null>(null)
  const [productLoadAttempt, setProductLoadAttempt] = useState(0)
  const [currentPrice, setCurrentPrice] = useState<number>(0)
  const [colourWays, setColourWays] = useState<IAddonItem[]>([])
  const [selectedColorWay, setSelectedColorWay] = useState<string | null>(null)
  const [detailColourwayAvailability, setDetailColourwayAvailability] = useState<Record<string, ColourwayAvailability>>({})
  const {
    isOpen: customColoursOpen,
    setOpen: setCustomColoursOpen,
    setCustomColourProduct,
    setCustomComponents,
    customComponents: globalCustomComponents,
    customColourProduct: globalCustomColourProduct,
    updateCustomComponent
  } = useCustomColoursStore()
  const [showConfiguratorModal, setShowConfiguratorModal] = useState<boolean>(false)
  const [isInitialLoad, setIsInitialLoad] = useState(true)
  const [showMoreColoursModal, setShowMoreColoursModal] = useState<boolean>(false)
  const [moreColoursData, setMoreColoursData] = useState<{ groupName: string; items: IAddonItem[] } | null>(null)

  const [addonsSelectedItemIds, setAddonsSelectedItemIds] = useState<ActiveAddonItem[]>([])
  const [addonsInputValues, setAddonsInputValues] = useState<AddonInputValue[]>([])
  const [imagesLoaded, setImagesLoaded] = useState(false);
  const [isAddingToBasket, setIsAddingToBasket] = useState(false)
  const lastStockCheckKeyRef = useRef<string | null>(null)
  const lastStockCheckAtRef = useRef<number | null>(null)
  const lastSelectedUnavailableKeyRef = useRef<string | null>(null)
  const lastAppliedStockKeyRef = useRef<string | null>(null)
  const latestProductRequestKeyRef = useRef<string | null>(null)
  const hasInitializedFromUrlRef = useRef(false)
  const initializedVariantRef = useRef<string | null>(null)
  const [stockById, setStockById] = useState<Record<string, StockCheckCalculatedStock>>({})
  const [globalStockNotice, setGlobalStockNotice] = useState<string | null>(null)
  const [holidayInfo, setHolidayInfo] = useState<import('../services/api').HolidayInfo | null>(null)
  const lastViewContentSignatureRef = useRef<string | null>(null)
  const ENGRAVING_CATALOGUE_ID = '67519d69197065b8a54a327e'
  const STOCK_CHECK_THROTTLE_MS = 2 * 60 * 1000
  // Ref so async callbacks always read current basket without re-creating memos/callbacks
  const basketItemsRef = useRef(basketItems)
  basketItemsRef.current = basketItems

  // Colorway-specific theming configurations
  const colorwayThemes: Record<string, ColorwayTheme> = {
    '6830b0df43132554b41a6b70': { // Butter Popcorn
      isDarkBackground: false,
      // tagline: 'Sweet Sounds, Sweeter Builds',
      // description: 'Our newest warm and creamy colourway that makes building headphones as delightful as movie night',
      backgroundColor: 'bg-gradient-to-br from-yellow-50 to-amber-50',
      navBackgroundColor: 'bg-gradient-to-r from-yellow-100 to-amber-100',
      accentColor: 'text-yellow-700',
      textColor: 'text-yellow-800',
      primaryTextColor: 'text-yellow-900',
      buttonVariant: 'solid-butter' as const,
      borderColor: 'border-yellow-200',
      images: [
        {
          url: 'https://d1tg5gdu842vo5.cloudfront.net/site-assets/images/headphones-in-context/girl_wearing_butter_popcorn.webp',
          showThumbnail: true,
          alt: 'Girl wearing Butter Popcorn Kibu Headphones'
        },
        {
          url: 'https://d1tg5gdu842vo5.cloudfront.net/site-assets/images/headphones-in-context/butter_popcorn_hero.webp',
          showThumbnail: true,
          alt: 'Butter Popcorn Kibu Headphones - Hero'
        },
        {
          url: 'https://d1tg5gdu842vo5.cloudfront.net/site-assets/images/headphones-in-context/butter_popcorn_parts.webp',
          showThumbnail: true,
          alt: 'Butter Popcorn Kibu Headphones - Parts'
        }
      ]
    },
    '683703f89b03e80f20e8bfb8': { // Midnight Berry
      isDarkBackground: true,
      // tagline: 'Dark Tones, Bright Futures',
      // description: 'Introducing our mysterious deep blue colourway - perfect for young audiophiles with sophisticated taste',
      backgroundColor: 'bg-gradient-to-br from-[#020017] to-indigo-950',
      navBackgroundColor: 'bg-gradient-to-r from-[#05003d] to-[#100c3d]',
      accentColor: 'text-blue-200',
      textColor: 'text-blue-100',
      primaryTextColor: 'text-blue-50',
      buttonVariant: 'solid-midnight' as const,
      borderColor: 'border-blue-900',
      images: [
        {
          url: 'https://d1tg5gdu842vo5.cloudfront.net/site-assets/images/headphones-in-context/boy_wearing_midnight_berry.webp',
          showThumbnail: true,
          alt: 'Boy wearing Midnight Berry Kibu Headphones'
        },
        {
          url: 'https://d1tg5gdu842vo5.cloudfront.net/site-assets/images/headphones-in-context/midnight_berry_hero.webp',
          showThumbnail: true,
          alt: 'Midnight Berry Kibu Headphones - Hero'
        },
        {
          url: 'https://d1tg5gdu842vo5.cloudfront.net/site-assets/images/headphones-in-context/midnight_berry_parts.webp',
          showThumbnail: true,
          alt: 'Midnight Berry Kibu Headphones - Parts'
        }
      ]
    },
    '6ab283a8a9c09ce409f2ee61': { // Dream Pop
      isDarkBackground: false,
      // tagline: 'Dreamy Tones, Whimsical Builds',
      // description: 'Our newest pastel colourway, blending candyfloss pink and lavender for a dreamy, whimsical build',
      backgroundColor: 'bg-gradient-to-br from-pink-100 to-pink-300',
      navBackgroundColor: 'bg-gradient-to-r from-pink-300 to-pink-300',
      accentColor: 'text-pink-500',
      textColor: 'text-pink-700',
      primaryTextColor: 'text-pink-900',
      buttonVariant: 'solid-dreampop' as const,
      borderColor: 'border-pink-200',
      images: [
        {
          url: 'https://d1tg5gdu842vo5.cloudfront.net/site-assets/product-items/6ab283a8a9c09ce409f2ee61/product_image/2026/09/22/dbd3e9e13b11438aa6676688c581a935-girl_wearing_dream_pop.webp',
          showThumbnail: true,
          alt: 'Girl wearing Dream Pop Kibu Headphones'
        },
        {
          url: 'https://d1tg5gdu842vo5.cloudfront.net/site-assets/product-items/6ab283a8a9c09ce409f2ee61/product_image/2026/09/22/24f7bcc3f7c1476dbf58b4cc0c4c8191-dream_pop_hero.webp',
          showThumbnail: true,
          alt: 'Dream Pop Kibu Headphones - Hero'
        },
        {
          url: 'https://d1tg5gdu842vo5.cloudfront.net/site-assets/product-items/6ab283a8a9c09ce409f2ee61/product_image/2026/09/22/c22db1fcc7734bf799f0274072f4da75-dream_pop_parts.webp',
          showThumbnail: true,
          alt: 'Dream Pop Kibu Headphones - Parts'
        }
      ]
    },
    '6ac9733f576416c12a6f3163': { // Turbo Dash
      isDarkBackground: false,
      backgroundColor: 'bg-gradient-to-br from-red-200 to-red-500',
      navBackgroundColor: 'bg-gradient-to-r from-red-400 to-red-500',
      accentColor: 'text-red-800',
      textColor: 'text-red-800',
      primaryTextColor: 'text-red-950',
      buttonVariant: 'solid-yellow' as const,
      borderColor: 'border-red-200',
      images: []
    },
    '6ac4fc4c3224c7952fc09d8a': { // Woodland Walk
      isDarkBackground: false,
      backgroundColor: 'bg-gradient-to-br from-lime-50 via-emerald-50 to-green-100',
      navBackgroundColor: 'bg-gradient-to-r from-lime-100 via-emerald-100 to-green-200',
      accentColor: 'text-emerald-700',
      textColor: 'text-emerald-800',
      primaryTextColor: 'text-emerald-950',
      buttonVariant: 'solid-green' as const,
      borderColor: 'border-emerald-200',
      images: []
    },
  };

  // Get current colorway theme
  const currentTheme = selectedColorWay ? colorwayThemes[selectedColorWay as keyof typeof colorwayThemes] : null;
  const stickyButtonVariantClassName = getVariantClasses(currentTheme?.buttonVariant ?? 'solid-orange')

  // Don't show prices while currency is loading (show spinner)
  // But product data loading is handled separately below
  // This just prevents showing stale prices during currency updates

  const regionForProduct = useMemo(
    () => (urlRegion || chosenRegion || storeRegion || 'gb').toLowerCase(),
    [urlRegion, chosenRegion, storeRegion]
  )

  useEffect(() => {
    const CACHE_KEY = `kibu_product_data_${productRequestId}_${regionForProduct}`
    const CACHE_DURATION = 1 * 60 * 10
    const cacheEnabled = (process.env.REACT_APP_ENVIRONMENT || process.env.ENVIRONMENT || 'prod').trim().toLowerCase() !== 'test'
    let cancelled = false
    const stripStockLimits = (productItem: IProduct): IProduct => ({
      ...productItem,
      stockLimits: undefined
    })
    const rememberAvailability = (groups: IOption[]) => {
      const availability: Record<string, ColourwayAvailability> = {}
      groups.forEach(group => group.options.forEach(item => {
        if (item.stockLimits) availability[item._id] = item.stockLimits
      }))
      setDetailColourwayAvailability(availability)
    }

    const fetchData = async () => {
      setIsLoading(true)
      setProductLoadError(null)
      const requestKey = `${productRequestId}|${regionForProduct}|${productLoadAttempt}`
      latestProductRequestKeyRef.current = requestKey
      try {
        // Check cache first
        const cachedData = cacheEnabled ? localStorage.getItem(CACHE_KEY) : null
        if (cachedData) {
          const { data, timestamp } = JSON.parse(cachedData)
          const isExpired = Date.now() - timestamp > CACHE_DURATION

          if (!isExpired) {
            console.log('[COLOUR] Using cached product data for product:', productRequestId)
            if (data.product) {
              const { availableOptions, ...restProduct } = data.product
              // Sort options by order before setting data
              const sortedOptions = availableOptions ? [...availableOptions].sort((a, b) => (a.order || 0) - (b.order || 0)) : []
              const sanitizedProduct = stripStockLimits(restProduct)
              const sanitizedOptions = sortedOptions.map(option => ({
                ...option,
                options: option.options.map(stripStockLimits)
              }))
              if (!cancelled && latestProductRequestKeyRef.current === requestKey) {
                rememberAvailability(sortedOptions)
                setProductData(sanitizedProduct, sanitizedOptions, data.materialInformation || [])
              }
              return
            }
          } else {
            console.log('[COLOUR] Cache expired for product:', productRequestId)
          }
        } else {
          console.log('[COLOUR] No cache found for product:', productRequestId)
        }

        // Fetch fresh data if no cache or cache expired
        // Use the user's chosen region to ensure x-country-code matches their selection
        const productData = await fetchProductData(productRequestId, regionForProduct)
        console.log('[regionModal] fetch product data region:', regionForProduct)
        console.log('[COLOUR] Fetched fresh product data for product:', productRequestId)
        if (productData.product) {
          const { availableOptions, ...restProduct } = productData.product
          // Sort options by order before setting data
          const sortedOptions = availableOptions ? [...availableOptions].sort((a, b) => (a.order || 0) - (b.order || 0)) : []
          console.log('[COLOUR] Setting product data with options:', sortedOptions)
          const sanitizedProduct = stripStockLimits(restProduct)
          const sanitizedOptions = sortedOptions.map(option => ({
            ...option,
            options: option.options.map(stripStockLimits)
          }))
          if (!cancelled && latestProductRequestKeyRef.current === requestKey) {
            rememberAvailability(sortedOptions)
            setProductData(sanitizedProduct, sanitizedOptions, productData.materialInformation || [])
          }

          // Cache the new data
          if (cacheEnabled && !cancelled) localStorage.setItem(CACHE_KEY, JSON.stringify({
            data: productData,
            timestamp: Date.now()
          }))
        }
      } catch (error) {
        console.error('[COLOUR] Error fetching product data:', error)
        if (!cancelled && latestProductRequestKeyRef.current === requestKey) {
          setProductLoadError(error instanceof Error ? error.message : 'Please try again in a moment.')
        }
      } finally {
        if (!cancelled && latestProductRequestKeyRef.current === requestKey) {
          setIsLoading(false)
        }
      }
    }

    fetchData()
    return () => { cancelled = true }
  }, [productRequestId, regionForProduct, productLoadAttempt])

  const productStockCheckCatalogueIds = useMemo(() => {
    const ids = new Set<string>()
    if (product?.productCatalogueId) {
      ids.add(String(product.productCatalogueId))
    }
    options.forEach((option) => {
      if (option.productCatalogueId) {
        ids.add(String(option.productCatalogueId))
      }
      option.options.forEach((optionProduct) => {
        if (optionProduct.productCatalogueId) {
          ids.add(String(optionProduct.productCatalogueId))
        }
      })
    })
    return Array.from(ids)
  }, [product, options])

  const mergeStockResponses = useCallback((responses: StockCheckResponse[]) => {
    const itemsMap = new Map<string, StockCheckItem>()
    const missingIds = new Set<string>()
    let notice: string | null = null
    let holiday: import('../services/api').HolidayInfo | null = null

    responses.forEach(response => {
      (response?.items || []).forEach(item => {
        const key = String(item.productItemId)
        const existing = itemsMap.get(key)
        if (!existing) {
          itemsMap.set(key, item)
          return
        }

        itemsMap.set(key, {
          ...existing,
          ...item,
          calculatedStock: {
            ...(existing.calculatedStock || {}),
            ...(item.calculatedStock || {})
          }
        })
      })
        ; (response?.missingIds || []).forEach(id => missingIds.add(String(id)))
      if (!notice && response?.globalProductNotice) {
        const trimmed = response.globalProductNotice.trim()
        if (trimmed) {
          notice = trimmed
        }
      }
      if (!holiday && response?.holidayInfo?.active) {
        holiday = response.holidayInfo
      }
    })

    return {
      items: Array.from(itemsMap.values()),
      missingIds: Array.from(missingIds),
      globalProductNotice: notice,
      holidayInfo: holiday,
    }
  }, [])

  const getStatusLabel = useCallback((status?: StockStatus, availableForSale?: boolean): string => {
    if (availableForSale === false) {
      return 'no longer available'
    }
    switch (status) {
      case 'LOW_STOCK':
        return 'low stock'
      case 'PREORDER':
        return 'preorder'
      case 'OUT_OF_STOCK':
        return 'sold out'
      case 'COMING_SOON':
        return 'coming soon'
      case 'BACK_SOON':
        return 'back soon'
      case 'NOT_YET_AVAILABLE':
        return 'not yet available'
      case 'NO_LONGER_AVAILABLE':
        return 'no longer available'
      case 'INSUFFICIENT_STOCK':
        return 'insufficient stock'
      case 'EXCEEDS_MAX_PER_ORDER':
        return 'limit reached'
      default:
        return 'unavailable'
    }
  }, [])

  const getProductLabel = useCallback((id: string): string => {
    for (const option of options) {
      const match = option.options.find(item => item._id === id)
      if (match) {
        return match.optionTitle || match.productTitle || 'Item'
      }
    }
    for (const item of basketItems) {
      if (item.colourWay._id === id) {
        return item.colourWay.optionTitle || item.colourWay.productTitle || item.name || 'Item'
      }
      const extraMatch = item.extras.find(extra => extra._id === id)
      if (extraMatch) {
        return extraMatch.optionTitle || extraMatch.productTitle || 'Item'
      }
    }
    return product?._id === id ? product.productTitle : 'Item'
  }, [options, basketItems, product])

  const getBasketUnavailableKey = (items: StockCheckItem[]) => {
    return items
      .map(item => `${item.productItemId}:${item.calculatedStock?.stockStatus ?? 'unknown'}:${item.calculatedStock?.availableForSale ?? 'unknown'}`)
      .sort()
      .join('|')
  }

  const buildUnavailableLightboxContent = useCallback((items: StockCheckItem[], scope: 'selection' | 'basket') => {
    const formattedItems = items.map(item => {
      const name = getProductLabel(item.productItemId)
      const status = getStatusLabel(item.calculatedStock?.stockStatus as StockStatus | undefined, item.calculatedStock?.availableForSale)
      return `${name} (${status})`
    })

    if (scope === 'selection') {
      return {
        title: formattedItems.length === 1 ? 'Sorry — item unavailable' : 'Sorry — items unavailable',
        message: formattedItems.length === 1
          ? 'That option just sold out. Please choose another option and we will take care of the rest.'
          : 'A few options just sold out. Please update your selection and we will take care of the rest.',
        items: formattedItems,
        removedFromBasket: false,
      }
    }

    return {
      title: formattedItems.length === 1 ? 'Sorry — item removed' : 'Sorry — items removed',
      message: formattedItems.length === 1
        ? 'It is no longer available, so it has been removed from your basket.'
        : 'Some items are no longer available, so they have been removed from your basket.',
      items: formattedItems,
      removedFromBasket: true,
    }
  }, [getProductLabel, getStatusLabel])

  const buildMissingStockItems = useCallback((missingIds: string[]) => {
    return missingIds.map(id => ({
      productItemId: id,
      calculatedStock: {
        stockStatus: 'OUT_OF_STOCK',
        availableForSale: false
      }
    } as StockCheckItem))
  }, [])

  const updateStockById = useCallback((items: StockCheckItem[]) => {
    setStockById(prev => {
      const next = { ...prev }
      items.forEach(item => {
        const key = String(item.productItemId)
        next[key] = item.calculatedStock || {}
      })
      return next
    })
  }, [])

  const isStockItemPurchasable = useCallback((item: StockCheckItem) => {
    return isPurchasableStatus(
      item.calculatedStock?.stockStatus as StockStatus | undefined,
      item.calculatedStock?.availableForSale
    )
  }, [])

  const mergeUnavailableItems = useCallback((items: StockCheckItem[], missingIds: string[], idSet: Set<string>) => {
    const combined = new Map<string, StockCheckItem>()

    items.forEach(item => {
      const key = String(item.productItemId)
      if (idSet.has(key)) {
        combined.set(key, item)
      }
    })

    buildMissingStockItems(missingIds.filter(id => idSet.has(String(id)))).forEach(item => {
      combined.set(String(item.productItemId), item)
    })

    return Array.from(combined.values())
  }, [buildMissingStockItems])

  const applyStockToProduct = useCallback((items: StockCheckItem[]) => {
    useProductDataStore.getState().applyCalculatedStock(new Map(items.map(item => [String(item.productItemId), item.calculatedStock])))
  }, [])

  useEffect(() => {
    if (!product || options.length === 0) {
      return
    }
    if (stockById && Object.keys(stockById).length > 0) {
      const colourwayIds = options.find(option => option.required)?.options.map(item => String(item._id)) || []
      const addonIds = options.filter(option => !option.required).flatMap(option => option.options.map(item => String(item._id)))
      console.log('[STOCK_DEBUG] stockById keys:', Object.keys(stockById))
      console.log('[STOCK_DEBUG] colourway IDs:', colourwayIds)
      console.log('[STOCK_DEBUG] addon IDs:', addonIds)
      console.log('[STOCK_DEBUG] colourway matches:', colourwayIds.map(id => ({ id, hasStock: Boolean(stockById[id]) })))
      console.log('[STOCK_DEBUG] addon matches:', addonIds.map(id => ({ id, hasStock: Boolean(stockById[id]) })))
    }
    const ids = Object.keys(stockById)
    if (ids.length === 0) {
      return
    }
    const key = ids.slice().sort().join(',')
    if (lastAppliedStockKeyRef.current === key) {
      return
    }
    const items = ids.map(id => ({
      productItemId: id,
      calculatedStock: stockById[id]
    }))
    applyStockToProduct(items)
    lastAppliedStockKeyRef.current = key
  }, [product, options, stockById, applyStockToProduct])

  const applyStockToBasket = useCallback((items: StockCheckItem[]) => {
    const currentBasketItems = basketItemsRef.current
    if (!items.length || !currentBasketItems.length) {
      return
    }

    const requestedCounts = new Map<string, number>()
    currentBasketItems.forEach((item) => {
      const colourId = String(item.colourWay._id)
      requestedCounts.set(colourId, (requestedCounts.get(colourId) || 0) + 1)
      item.extras.forEach((extra) => {
        const extraId = String(extra._id)
        requestedCounts.set(extraId, (requestedCounts.get(extraId) || 0) + 1)
      })
    })

    const normalizedStockMap = buildStockMapWithSharedPoolPreorder(items, requestedCounts)

    currentBasketItems.forEach(item => {
      const hasUpdates = normalizedStockMap.has(String(item.colourWay._id)) || item.extras.some(extra => normalizedStockMap.has(String(extra._id)))
      if (!hasUpdates) {
        return
      }
      const updatedColourWay = mergeCalculatedStock(item.colourWay, normalizedStockMap.get(String(item.colourWay._id)))
      const updatedExtras = item.extras.map(extra => mergeCalculatedStock(extra, normalizedStockMap.get(String(extra._id))))
      const hasColourwayUpdate = JSON.stringify(updatedColourWay.stockLimits) !== JSON.stringify(item.colourWay.stockLimits)
      const hasExtrasUpdate = updatedExtras.some((extra, index) => (
        JSON.stringify(extra.stockLimits) !== JSON.stringify(item.extras[index]?.stockLimits)
      ))

      if (hasColourwayUpdate || hasExtrasUpdate) {
        updateBasketItem(item.id, { colourWay: updatedColourWay, extras: updatedExtras })
      }
    })
  }, [updateBasketItem])

  useEffect(() => {
    if (!basketItems.length) {
      return
    }
    const ids = Object.keys(stockById)
    if (ids.length === 0) {
      return
    }

    const items = ids.map(id => ({
      productItemId: id,
      calculatedStock: stockById[id]
    }))
    applyStockToBasket(items)
  }, [basketItems, stockById, applyStockToBasket])

  const basketRequestedCounts = useMemo(() => {
    const counts: Record<string, number> = {}
    basketItems.forEach((item) => {
      const colourId = String(item.colourWay._id)
      counts[colourId] = (counts[colourId] || 0) + 1
      item.extras.forEach((extra) => {
        const extraId = String(extra._id)
        counts[extraId] = (counts[extraId] || 0) + 1
      })
    })
    return counts
  }, [basketItems])

  const hasSharedPoolShortageForAdd = useCallback((
    combinedStockItems: StockCheckItem[],
    pendingSelectionIds: string[]
  ): {
    hasShortage: boolean
    allowsPreorder: boolean
    title: string
    message: string
    items: string[]
    preorderPoolIds: string[]
  } => {
    const requestedCounts = new Map<string, number>()

    // Existing basket demand
    basketItemsRef.current.forEach((item) => {
      requestedCounts.set(String(item.colourWay._id), (requestedCounts.get(String(item.colourWay._id)) || 0) + 1)
      item.extras.forEach((extra) => {
        requestedCounts.set(String(extra._id), (requestedCounts.get(String(extra._id)) || 0) + 1)
      })
    })

    // New item demand being added now
    pendingSelectionIds.forEach((id) => {
      const key = String(id)
      requestedCounts.set(key, (requestedCounts.get(key) || 0) + 1)
    })

    const stockMap = new Map<string, StockCheckItem>()
    combinedStockItems.forEach((item) => {
      stockMap.set(String(item.productItemId), item)
    })

    const pools = new Map<string, {
      totalRequested: number
      totalAvailable: number
      requestedIds: Set<string>
    }>()

    requestedCounts.forEach((requestedQty, productItemId) => {
      const sharedPool = stockMap.get(productItemId)?.calculatedStock?.sharedPool
      if (!sharedPool || typeof sharedPool.totalAvailable !== 'number') {
        return
      }

      const poolMembers = new Set<string>([
        productItemId,
        ...(sharedPool.siblingProductItemIds || []).map(String)
      ])
      const poolKey = Array.from(poolMembers).sort().join('|')

      if (!pools.has(poolKey)) {
        pools.set(poolKey, {
          totalRequested: 0,
          totalAvailable: sharedPool.totalAvailable,
          requestedIds: new Set<string>()
        })
      }

      const pool = pools.get(poolKey)!
      pool.totalRequested += requestedQty
      pool.requestedIds.add(productItemId)
    })

    for (const pool of pools.values()) {
      if (pool.totalRequested <= pool.totalAvailable) {
        continue
      }

      const removeCount = Math.max(pool.totalRequested - pool.totalAvailable, 1)
      const itemLines = Array.from(pool.requestedIds).map((id) => {
        const label = getProductLabel(id)
        const qty = requestedCounts.get(id) || 0
        return qty > 1 ? `${label} x${qty}` : label
      })
      const poolAllowsPreorder = Array.from(pool.requestedIds).some((id) =>
        stockMap.get(id)?.calculatedStock?.canPreorder === true
      )

      if (poolAllowsPreorder) {
        return {
          hasShortage: false,
          allowsPreorder: true,
          title: 'Heads up — additional units will be preorder',
          message: `In-stock allocation is ${pool.totalAvailable} for this shared pool, and you now have ${pool.totalRequested}. The extra ${removeCount} item${removeCount > 1 ? 's' : ''} will be preordered and everything will be delivered together when stock arrives.`,
          items: itemLines,
          preorderPoolIds: Array.from(pool.requestedIds),
        }
      }

      return {
        hasShortage: true,
        allowsPreorder: false,
        title: 'Limited shared stock',
        message: `These items share one stock pool. You requested ${pool.totalRequested}, but only ${pool.totalAvailable} can be purchased now. Remove ${removeCount} item${removeCount > 1 ? 's' : ''} and try again.`,
        items: itemLines,
        preorderPoolIds: [],
      }
    }

    return {
      hasShortage: false,
      allowsPreorder: false,
      title: '',
      message: '',
      items: [],
      preorderPoolIds: []
    }
  }, [getProductLabel])

  const runStockCheck = useCallback(async (force: boolean) => {
    const basketStockCheckCatalogueIds = (() => {
      const ids = new Set<string>()
      basketItemsRef.current.forEach(item => {
        if (item.colourWay.productCatalogueId) {
          ids.add(String(item.colourWay.productCatalogueId))
        }
        item.extras.forEach(extra => {
          if (extra.productCatalogueId) {
            ids.add(String(extra.productCatalogueId))
          }
        })
      })
      return Array.from(ids)
    })()

    if (!productStockCheckCatalogueIds.length && !basketStockCheckCatalogueIds.length) {
      return
    }

    const now = Date.now()
    if (!force && lastStockCheckAtRef.current && now - lastStockCheckAtRef.current < STOCK_CHECK_THROTTLE_MS) {
      return
    }

    const key = [...productStockCheckCatalogueIds, ...basketStockCheckCatalogueIds].sort().join(',')
    if (!force && lastStockCheckKeyRef.current === key) {
      return
    }
    lastStockCheckKeyRef.current = key
    lastStockCheckAtRef.current = now

    try {
      const promises: Promise<StockCheckResponse>[] = []
      if (productStockCheckCatalogueIds.length) {
        promises.push(fetchStockCheck({ productCatalogue: productStockCheckCatalogueIds }))
      }
      if (basketStockCheckCatalogueIds.length) {
        promises.push(fetchStockCheck({ productCatalogue: basketStockCheckCatalogueIds }))
      }
      const responses = await Promise.all(promises)
      const stockData = mergeStockResponses(responses)
      setGlobalStockNotice(stockData.globalProductNotice ?? null)
      setHolidayInfo(stockData.holidayInfo ?? null)
      if (stockData.items.length === 0 && stockData.missingIds.length === 0) {
        return
      }
      const combinedStockItems = [...stockData.items, ...buildMissingStockItems(stockData.missingIds)]
      updateStockById(combinedStockItems)
      applyStockToProduct(combinedStockItems)
      applyStockToBasket(combinedStockItems)

      const selectedColourwayIds = new Set(selectedColorWay ? [String(selectedColorWay)] : [])
      const unavailableSelectedItems = mergeUnavailableItems(
        combinedStockItems.filter(item => !isStockItemPurchasable(item)),
        [],
        selectedColourwayIds
      )

      if (unavailableSelectedItems.length) {
        const unavailableKey = getBasketUnavailableKey(unavailableSelectedItems)
        if (lastSelectedUnavailableKeyRef.current !== unavailableKey) {
          lastSelectedUnavailableKeyRef.current = unavailableKey
          showStockNotice({
            ...buildUnavailableLightboxContent(unavailableSelectedItems, 'selection'),
            onClose: () => runStockCheck(true)
          })
        }
      } else {
        lastSelectedUnavailableKeyRef.current = null
      }
    } catch (error) {
      console.error('[COLOUR] Error fetching stock data:', error)
    }
  }, [productStockCheckCatalogueIds, selectedColorWay, applyStockToProduct, applyStockToBasket, getBasketUnavailableKey, buildUnavailableLightboxContent, showStockNotice, mergeUnavailableItems, isStockItemPurchasable, updateStockById, mergeStockResponses])

  const handleBlockedAddonSelect = useCallback((item: IAddonItem) => {
    const sharedMessage = item.sharedPoolMessage
    showStockNotice({
      title: sharedMessage ? 'Limited shared stock' : 'Option unavailable',
      message: sharedMessage
        ? `${item.title} can’t be selected right now. ${sharedMessage} Remove one from your basket to continue.`
        : `${item.title} is currently unavailable. Please choose another option.`,
      items: [item.title],
      removedFromBasket: false,
      onClose: () => runStockCheck(true)
    })
  }, [showStockNotice, runStockCheck])

  useEffect(() => {
    let isCancelled = false
    const fetchStock = async () => {
      if (isCancelled) {
        return
      }
      await runStockCheck(false)
    }

    fetchStock()

    return () => {
      isCancelled = true
    }
  }, [runStockCheck])

  useEffect(() => {
    const refresh = () => { if (document.visibilityState === 'visible') void runStockCheck(true) }
    const interval = window.setInterval(refresh, 60000)
    window.addEventListener('focus', refresh)
    document.addEventListener('visibilitychange', refresh)
    return () => {
      window.clearInterval(interval)
      window.removeEventListener('focus', refresh)
      document.removeEventListener('visibilitychange', refresh)
    }
  }, [runStockCheck])

  useEffect(() => {
    const _colourWays: IProduct[] = options.find(option => option.required)?.options || []
    if (_colourWays) {
      console.log('[COLOUR] Processing colourways from options:', options)
      const foundCustomColourProduct: IProduct | undefined = _colourWays.find((_product: IProduct) => _product.optionTitle?.toLowerCase().includes('custom'))
      console.log('[COLOUR] Raw colourways before mapping:', _colourWays.map(cw => ({ id: cw._id, title: cw.optionTitle, price: cw.price?.unit_amount, rank: cw.rank })))

      // Sort all colourways purely by rank
      const sortedByRank = rankColourways(_colourWays);
      const sortedColourWayItems: IAddonItem[] = sortedByRank.map((_product: IProduct) => {
        const unitAmount = _product.price?.unit_amount ?? 0
        const item: IAddonItem = {
          id: _product._id,
          imageUrl: getProductThumbnailUrl(_product) || getLegacyThumbnailPath(_product._id),
          title: _product.optionTitle || '',
          price: 0,
          currency: currencySymbol,
          triggersSpecialSelect: _product.optionTitle?.toLowerCase().includes('custom'),
          originalPrice: unitAmount,
          group: _product.group
        }
        return item
      })

      console.log('[COLOUR] Final colourways (sorted purely by rank):', sortedColourWayItems.map(cw => ({
        id: cw.id,
        title: cw.title,
        rank: _colourWays.find(c => c._id === cw.id)?.rank,
        group: cw.group,
        triggersSpecialSelect: cw.triggersSpecialSelect
      })))

      setColourWays(sortedColourWayItems)
      setCustomColourProduct(foundCustomColourProduct)

      // Initialize custom components if we have a custom colour product
      if (foundCustomColourProduct?.components) {
        const initialComponents: ICustomComponent[] = foundCustomColourProduct.components.map((component: ICustomComponent) => ({
          componentVariantId: component.componentVariantId,
          componentType: component.componentType,
          name: component.name,
          position: component.position || null,
          chosenMaterial: { $oid: component.chosenMaterial.$oid || '' },
          display: component.display,
          materialOptions: component.materialOptions
        }))
        setCustomComponents(initialComponents)
      }
    } else {
      console.log('[COLOUR] No colourways found in options:', options)
    }
  }, [product, options, currencySymbol])

  useEffect(() => {
    const _addonsActiveItemIds: ActiveAddonItem[] = options.map((option: IOption) => ({
      [option.productCatalogueId]: null,
    }))
    setAddonsSelectedItemIds(_addonsActiveItemIds)
  }, [options])

  useEffect(() => {
    if (isLoading || !colourWays.length) {
      return
    }

    const items = options.find(option => option.required)?.options || []
    if (initializedVariantRef.current !== productId) {
      initializedVariantRef.current = productId
      hasInitializedFromUrlRef.current = false
    }
    const availability = { ...detailColourwayAvailability, ...stockById }
    const next = nextColourway(items, selectedColorWay, productId, hasInitializedFromUrlRef.current, availability)
    if (next) {
      setSelectedColorWay(next.id)
      setIsInitialLoad(true)
      hasInitializedFromUrlRef.current = true
    }
  }, [colourWays, productId, selectedColorWay, options, isLoading, detailColourwayAvailability, stockById])

  // Handle delayed opening of custom colours on initial load
  useEffect(() => {
    if (isInitialLoad && selectedColorWay === CUSTOM_COLORS_ID && productId === CUSTOM_COLORS_ID) {
      console.log('[COLOUR] Setting up delayed modal open for custom colors')
      const timer = setTimeout(() => {
        console.log('[COLOUR] Opening configurator modal after delay')
        setShowConfiguratorModal(true)
        setIsInitialLoad(false)  // Reset the initial load flag
      }, 2000)
      return () => clearTimeout(timer)
    }
  }, [selectedColorWay, isInitialLoad, productId])

  const selectedColourwayProduct = useMemo(() => {
    const colourwayOptions = options.find(option => option.required)?.options || []
    return colourwayOptions.find(option => option._id === selectedColorWay) || null
  }, [options, selectedColorWay])

  const selectedColourwayImages = useMemo(() => {
    const fallbackAlt =
      selectedColourwayProduct?.optionTitle ||
      selectedColourwayProduct?.productTitle ||
      productsData[0]?.name ||
      'Kibu Headphones'
    const images = (selectedColourwayProduct as { images?: unknown; heroImage?: unknown } | null)?.images
    const heroImage = (selectedColourwayProduct as { images?: unknown; heroImage?: unknown } | null)?.heroImage
    const normalizedImages = normalizeProductImages(images, fallbackAlt)
    const normalizedHero = normalizeHeroImage(heroImage, fallbackAlt)
    if (!normalizedHero) {
      return normalizedImages
    }

    const filteredImages = normalizedImages.filter(image => image.url !== normalizedHero.url)
    return [normalizedHero, ...filteredImages]
  }, [selectedColourwayProduct])

  const customHeroImage = useMemo(() => {
    if (selectedColorWay !== CUSTOM_COLORS_ID) {
      return null
    }

    const alt =
      globalCustomColourProduct?.optionTitle ||
      globalCustomColourProduct?.productTitle ||
      'Custom Colours'
    return {
      url: `/assets/images/product_images/${CUSTOM_COLORS_ID}_main.webp`,
      backgroundUrl: `/assets/images/product_images/${CUSTOM_COLORS_ID}_background.webp`,
      showThumbnail: true,
      alt,
    } as IProductImage
  }, [CUSTOM_COLORS_ID, selectedColorWay, globalCustomColourProduct])

  const fallbackImages = currentTheme?.images || productsData[0].images
  const baseImages = selectedColourwayImages.length ? selectedColourwayImages : fallbackImages
  const filteredBaseImages = customHeroImage
    ? baseImages.filter(image => image.url !== customHeroImage.url)
    : baseImages
  const activeProductImage = customHeroImage || filteredBaseImages[0] || productsData[0].images[0]
  const slideshowImages = customHeroImage
    ? filteredBaseImages
    : filteredBaseImages.slice(1)
  const customHeroData = customHeroImage && globalCustomColourProduct && globalCustomComponents.length
    ? {
      backgroundUrl: customHeroImage.backgroundUrl,
      customColourProduct: globalCustomColourProduct,
      customComponents: globalCustomComponents
    }
    : undefined

  const getColourwayName = (base: string, full: string): string => {
    if (full.startsWith(base)) {
      const result = full.slice(base.length).trimStart()
      return result.startsWith("- ") ? result.slice(2).trimStart() : result
    }
    return full
  }

  const getActiveId = useCallback((optionId: string) => {
    const foundItem = addonsSelectedItemIds.find((item) => item.hasOwnProperty(optionId))
    return foundItem ? foundItem[optionId] : null
  }, [addonsSelectedItemIds])

  const handleAddonSelect = useCallback((optionId: string, value: string | null) => {
    // If selecting the custom colors option, open the editor immediately (no delay)
    const selectedOption = options.find(opt => opt.productCatalogueId === optionId)
    const selectedProduct = selectedOption?.options.find(opt => opt._id === value)

    // If clicking the custom colors option when it's already selected, reopen the editor
    if (value === selectedColorWay && selectedProduct?.optionTitle?.toLowerCase().includes('custom')) {
      setIsInitialLoad(false)  // Ensure this is marked as user selection
      setCustomColoursOpen(true)
      return
    }

    // If selecting the custom colors option for the first time
    if (selectedProduct?.optionTitle?.toLowerCase().includes('custom')) {
      setIsInitialLoad(false)  // Ensure this is marked as user selection
      setCustomColoursOpen(true)
      return
    }

    setAddonsSelectedItemIds((prevItems) =>
      prevItems.map((item) => (item.hasOwnProperty(optionId) ? { [optionId]: value } : item))
    )
  }, [options, selectedColorWay])

  const handleAddonInputChange = useCallback((optionId: string, itemId: string, inputValue: string) => {
    const newValue: AddonInputValue = {
      optionId: optionId,
      itemId: itemId,
      inputValue: inputValue
    }
    setAddonsInputValues((prevValues) => [
      ...prevValues.filter(aiv => aiv.optionId !== optionId || aiv.itemId !== itemId),
      newValue
    ])
  }, [])

  // Create memoized input change handlers for each option to prevent infinite loops
  const inputChangeHandlers = useMemo(() => {
    const handlers: Record<string, (itemId: string, inputValue: string) => void> = {}

    options.forEach(option => {
      handlers[option.productCatalogueId] = (itemId: string, inputValue: string) => {
        // Only allow valid characters for engraving
        if (option.productCatalogueId === '67519d69197065b8a54a327e') {
          const validInput = inputValue.replace(/[^A-Za-z0-9\s.,!?\-'/]/g, '')
          handleAddonInputChange(option.productCatalogueId, itemId, validInput)
        } else {
          handleAddonInputChange(option.productCatalogueId, itemId, inputValue)
        }
      }
    })

    return handlers
  }, [options, handleAddonInputChange])

  useEffect(() => {
    let price: number = 0
    let currency: string = ''
    const activeAddonOptionIds: string[] = addonsSelectedItemIds.flatMap(item =>
      Object.values(item).filter((value): value is string => value !== null)
    )

    for (const option of options) {
      for (const product of option.options) {
        if (product._id === selectedColorWay) {
          price += product.price?.unit_amount ?? 0
          currency = currencySymbol
        } else if (activeAddonOptionIds.includes(product._id)) {
          // Check if this addon is free due to discount
          const isFree = isAddonFree(product._id, option.productCatalogueId)
          if (!isFree) {
            price += product.price?.unit_amount ?? 0
          }
          currency = currencySymbol
        }
      }
    }

    setCurrentPrice(price)
  }, [selectedColorWay, addonsSelectedItemIds, discountCode, currencySymbol, options])

  const getSelectedAddonsAsProduct = (ids: string[]): IProduct[] => {
    const products: IProduct[] = []

    for (const option of options) {
      for (const product of option.options) {
        if (ids.includes(product._id)) {
          products.push(product)
        }
      }
    }

    return products
  }

  useEffect(() => {
    if (isLoading || !options.length) {
      return
    }

    const colourwayOptions = options.find(option => option.required)?.options || []
    const activeColourway = colourwayOptions.find(option => option._id === selectedColorWay) || colourwayOptions[0]
    if (!activeColourway) {
      return
    }

    const activeAddonOptionIds: string[] = addonsSelectedItemIds.flatMap(item =>
      Object.values(item).filter((value): value is string => value !== null)
    )
    const hasEngravingValue = addonsInputValues.some((inputValue) => (
      inputValue.optionId === ENGRAVING_CATALOGUE_ID
      && activeAddonOptionIds.includes(inputValue.itemId)
      && inputValue.inputValue.trim().length > 0
    ))

    const signature = JSON.stringify({
      colourwayId: activeColourway._id,
      addonIds: [...activeAddonOptionIds].sort(),
      price: currentPrice,
      hasEngravingValue,
      region: regionForProduct,
    })

    if (signature === lastViewContentSignatureRef.current) {
      return
    }

    const timeout = setTimeout(() => {
      trackViewContent(
        activeColourway,
        regionForProduct.toUpperCase(),
        currentPrice,
        activeColourway.price?.currency
      )
      lastViewContentSignatureRef.current = signature
    }, 700)

    return () => clearTimeout(timeout)
  }, [
    isLoading,
    options,
    selectedColorWay,
    addonsSelectedItemIds,
    addonsInputValues,
    currentPrice,
    regionForProduct,
  ])

  const handleConfiguratorChoice = (openConfigurator: boolean) => {
    setShowConfiguratorModal(false)
    if (openConfigurator) {
      setCustomColoursOpen(true)
      // Track start of customization
      if (globalCustomColourProduct) {
        trackStartCustomization(globalCustomColourProduct, chosenRegion)
      }
    }
  }

  useEffect(() => {
    if (customColoursOpen) {
      toggleOverlay(true)
      setNavVisibilityOverride(false)
      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Escape' && validateCustomComponents()) {
          setCustomColoursOpen(false)
        }
      }
      document.addEventListener('keydown', handleKeyDown)
      return () => {
        document.removeEventListener('keydown', handleKeyDown)
        setNavVisibilityOverride(null)
      }
    } else {
      toggleOverlay(false)
      setNavVisibilityOverride(null)
    }
  }, [customColoursOpen])

  const handleCustomColourClose = () => {
    // Reset to the first ranked purchasable standard colour.
    if (colourWays.length > 0) {
      const items = options.find(option => option.required)?.options || []
      const standardIds = new Set(colourWays.filter(cw => !cw.triggersSpecialSelect).map(cw => cw.id))
      const choice = chooseRankedColourway(items.filter(item => standardIds.has(item._id)), null,
        { ...detailColourwayAvailability, ...stockById })
      setSelectedColorWay(choice?._id ?? null)
    }
    setCustomColoursOpen(false)
  }

  const handleCustomColourComplete = () => {
    // Set the custom colourway as selected when completing
    const customColourway = colourWays.find(cw => cw.triggersSpecialSelect)
    if (customColourway && globalCustomColourProduct) {
      setSelectedColorWay(customColourway.id)
      // Track completion of customization
      trackCompleteCustomization(globalCustomColourProduct, globalCustomComponents, chosenRegion)
    }
    setCustomColoursOpen(false)
  }

  const colourwayItems = options.find(option => option.required)?.options || []
  const colourwayAvailability = { ...detailColourwayAvailability, ...stockById }
  const visibleColourWays = colourWays.filter(colour => {
    const item = colourwayItems.find(item => item._id === colour.id)
    return item && canDisplayColourway(item)
  })
  const rankedAvailableColourway = chooseRankedColourway(colourwayItems, null, colourwayAvailability)
  const hasListedColourways = colourwayItems.some(item => canDisplayColourway(item))
  const allListedColourwaysUnavailable = hasListedColourways
    && !colourwayItems.some(item => canSelectColourway(item, colourwayAvailability))
  const displayPrice = allListedColourwaysUnavailable
    ? rankColourways(colourwayItems).find(item => canDisplayColourway(item))?.price?.unit_amount ?? currentPrice
    : currentPrice
  const selectedPurchasableColourway = colourwayItems.find(item => item._id === selectedColorWay
    && canSelectColourway(item, colourwayAvailability))
  const hasReadyColourway = Boolean(selectedPurchasableColourway && currentPrice > 0)

  const handleAddToBasket = async () => {
    if (isLoading || productLoadError || isAddingToBasket || !hasReadyColourway) {
      return
    }
    setIsAddingToBasket(true)
    const colourWay: IProduct | undefined = options[0]?.options.find(cw => cw._id === selectedColorWay)
    if (!colourWay) {
      setIsAddingToBasket(false)
      return
    }

    const selectedOptionIds: string[] = addonsSelectedItemIds.flatMap(item =>
      Object.values(item).filter((value): value is string => value !== null)
    )
    const selectedOptions: IProduct[] = getSelectedAddonsAsProduct(selectedOptionIds)
    let latestStockMap: Map<string, StockCheckCalculatedStock> | null = null
    let sharedPoolCheck = {
      hasShortage: false,
      allowsPreorder: false,
      title: '',
      message: '',
      items: [] as string[],
      preorderPoolIds: [] as string[],
    }

    let stockCheckSucceeded = false
    try {
      // Include currently selected addon IDs alongside existing basket IDs so addons
      // that have never been in the basket are still checked before first add.
      const currentBasketCatalogueIds = basketItems.flatMap(item => [
        item.colourWay.productCatalogueId,
        ...item.extras.map(extra => extra.productCatalogueId)
      ]).filter((id): id is string => Boolean(id))
      const selectedOptionCatalogueIds = selectedOptions
        .map(item => item.productCatalogueId)
        .filter((id): id is string => Boolean(id))
      const allCatalogueToCheck = [
        ...new Set([colourWay.productCatalogueId, ...currentBasketCatalogueIds, ...selectedOptionCatalogueIds].filter((id): id is string => Boolean(id)))
      ]
      const key = [...productStockCheckCatalogueIds, ...allCatalogueToCheck].sort().join(',')
      const promises: Promise<StockCheckResponse>[] = []
      if (productStockCheckCatalogueIds.length) {
        promises.push(fetchStockCheck({ productCatalogue: productStockCheckCatalogueIds }))
      }
      if (allCatalogueToCheck.length) {
        promises.push(fetchStockCheck({ productCatalogue: allCatalogueToCheck }))
      }
      const responses = await Promise.all(promises)
      const stockData = mergeStockResponses(responses)
      if (key) {
        lastStockCheckKeyRef.current = key
        lastStockCheckAtRef.current = Date.now()
      }
      if (stockData.items.length || stockData.missingIds.length) {
        const combinedStockItems = [...stockData.items, ...buildMissingStockItems(stockData.missingIds)]
        latestStockMap = new Map(
          combinedStockItems.map(item => [String(item.productItemId), item.calculatedStock as StockCheckCalculatedStock])
        )
        updateStockById(combinedStockItems)
        applyStockToProduct(combinedStockItems)
        applyStockToBasket(combinedStockItems)
      }
      if (stockData.globalProductNotice !== undefined) {
        setGlobalStockNotice(stockData.globalProductNotice ?? null)
      }
      if (stockData.holidayInfo !== undefined) {
        setHolidayInfo(stockData.holidayInfo ?? null)
      }
      stockCheckSucceeded = true

      const selectionIds = new Set([String(colourWay._id), ...selectedOptionIds.map(id => String(id))])
      const combinedStockItems = [...stockData.items, ...buildMissingStockItems(stockData.missingIds)]
      const unavailableSelectionItems = mergeUnavailableItems(
        combinedStockItems.filter(item => !isStockItemPurchasable(item)),
        [],
        selectionIds
      )

      if (unavailableSelectionItems.length) {
        showStockNotice({
          ...buildUnavailableLightboxContent(unavailableSelectionItems, 'selection'),
          onClose: () => runStockCheck(true)
        })
        setIsAddingToBasket(false)
        return
      }

      sharedPoolCheck = hasSharedPoolShortageForAdd(
        combinedStockItems,
        [String(colourWay._id), ...selectedOptionIds.map(id => String(id))]
      )

      if (sharedPoolCheck.hasShortage) {
        showStockNotice({
          title: sharedPoolCheck.title,
          message: sharedPoolCheck.message,
          items: sharedPoolCheck.items,
          removedFromBasket: false,
          onClose: () => runStockCheck(true)
        })
        setIsAddingToBasket(false)
        return
      }

      if (sharedPoolCheck.allowsPreorder) {
        const preorderIds = new Set(sharedPoolCheck.preorderPoolIds.map(String))
        basketItemsRef.current.forEach((item) => {
          const shouldUpdateColourway = preorderIds.has(String(item.colourWay._id))
          const updatedExtras = item.extras.map((extra) => {
            if (!preorderIds.has(String(extra._id))) {
              return extra
            }
            return mergeCalculatedStock(extra, {
              stockStatus: 'PREORDER',
              isPreorder: true,
              availableForSale: true
            })
          })
          const hasExtrasChanged = updatedExtras.some((extra, index) => extra !== item.extras[index])
          if (shouldUpdateColourway || hasExtrasChanged) {
            const updatedColourWay = shouldUpdateColourway
              ? mergeCalculatedStock(item.colourWay, {
                stockStatus: 'PREORDER',
                isPreorder: true,
                availableForSale: true
              })
              : item.colourWay
            updateBasketItem(item.id, { colourWay: updatedColourWay, extras: updatedExtras })
          }
        })

        showStockNotice({
          title: sharedPoolCheck.title,
          message: sharedPoolCheck.message,
          items: sharedPoolCheck.items,
          removedFromBasket: false,
          onClose: () => runStockCheck(true)
        })
      }

    } catch (error) {
      console.error('[COLOUR] Error fetching stock data before add to basket:', error)
    }

    if (!stockCheckSucceeded) {
      console.warn('[COLOUR] Stock check failed; proceeding with add to basket.')
    }

    let price: number = colourWay.price?.unit_amount ?? 0
    selectedOptions.forEach(option => {
      price += option.price?.unit_amount ?? 0
    })
    const basketColourWay = latestStockMap
      ? mergeCalculatedStock(colourWay, latestStockMap.get(String(colourWay._id)))
      : colourWay
    const basketExtras = latestStockMap
      ? selectedOptions.map(option => mergeCalculatedStock(option, latestStockMap?.get(String(option._id))))
      : selectedOptions
    const preorderPoolIds = new Set(sharedPoolCheck.preorderPoolIds.map(String))
    const finalBasketColourWay = preorderPoolIds.has(String(basketColourWay._id))
      ? mergeCalculatedStock(basketColourWay, {
        stockStatus: 'PREORDER',
        isPreorder: true,
        availableForSale: true
      })
      : basketColourWay
    const finalBasketExtras = basketExtras.map((option) => (
      preorderPoolIds.has(String(option._id))
        ? mergeCalculatedStock(option, {
          stockStatus: 'PREORDER',
          isPreorder: true,
          availableForSale: true
        })
        : option
    ))

    const basketItem: IBasketItem = {
      id: uuidv4(),
      name: finalBasketColourWay.productTitle,
      colourWay: {
        ...finalBasketColourWay,
        availableOptions: options.length
          ? options
          : (product?.availableOptions || finalBasketColourWay.availableOptions)
      },
      price: price,
      extras: finalBasketExtras,
      extrasData: addonsInputValues
    }

    addItemToBasket(basketItem)
    // Track AddToCart event
    trackAddToCart(basketItem, chosenRegion)
    setBasketOpen(true)
    setIsAddingToBasket(false)
  }

  const hasStringModification = (partsModifications: IPartsModifications): boolean => {
    const result: boolean = Object.values(partsModifications).some(modification => modification.type === "string")
    return result
  }

  const getScrollOffsetY = (): number => window.scrollY

  const validateCustomComponents = (): boolean => {
    return globalCustomComponents.every(component => component.chosenMaterial.$oid !== '')
  }

  // Add this effect to handle body scroll locking for the more colours modal
  useEffect(() => {
    if (showMoreColoursModal && contentRef.contentRef?.current) {
      const scrollContainer = contentRef.contentRef.current;
      const originalOverflow = scrollContainer.style.overflow;
      const originalScrollTop = scrollContainer.scrollTop; // Store current scroll position

      // Scroll to top and lock scrolling
      scrollContainer.scrollTop = 0;
      scrollContainer.style.overflow = 'hidden';

      return () => {
        scrollContainer.style.overflow = originalOverflow;
        // Optionally restore scroll position (comment out if you want to stay at top)
        scrollContainer.scrollTop = originalScrollTop;
      };
    }
  }, [showMoreColoursModal, contentRef]);

  // Add this effect to handle body scroll locking
  useEffect(() => {
    if (showConfiguratorModal) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'auto'
    }

    return () => {
      document.body.style.overflow = 'auto'
    }
  }, [showConfiguratorModal])

  const formattedUpdateDate = (() => {
    const rawDate = countryDetails?.returns?.updateDate
    if (!rawDate) return '28th January 2025'
    try {
      const parsed = new Date(rawDate)
      if (Number.isNaN(parsed.getTime())) {
        return '28th January 2025'
      }
      return format(parsed, "do MMMM yyyy")
    } catch {
      return '28th January 2025'
    }
  })()
  const selectedColourwayForDispatch =
    selectedColourwayProduct || options[0]?.options.find(cw => cw._id === selectedColorWay) || null
  const baseDispatchEstimate = product?.stockLimits?.estimatedDispatchDays || null
  const selectedDispatchEstimate = selectedColourwayForDispatch?.stockLimits?.estimatedDispatchDays || null
  const extraDispatchDays = selectedColourwayForDispatch?.stockLimits?.additionalDispatchDays ?? null
  const isPreorder = selectedColourwayForDispatch?.stockLimits?.isPreorder === true
    || selectedColourwayForDispatch?.stockLimits?.stockStatus === 'PREORDER'
  const preorderMessage = globalStockNotice?.trim() ? globalStockNotice : undefined
  const dispatchEstimate = selectedDispatchEstimate
    ? selectedDispatchEstimate
    : baseDispatchEstimate
      ? {
        min: typeof baseDispatchEstimate.min === 'number'
          ? baseDispatchEstimate.min + (extraDispatchDays ?? 0)
          : baseDispatchEstimate.min,
        max: typeof baseDispatchEstimate.max === 'number'
          ? baseDispatchEstimate.max + (extraDispatchDays ?? 0)
          : baseDispatchEstimate.max,
        unit: baseDispatchEstimate.unit || 'days'
      }
      : typeof extraDispatchDays === 'number'
        ? { min: extraDispatchDays, max: extraDispatchDays, unit: 'days' }
        : null

  const region = regionForProduct
  const canonicalUrl = toAbsoluteSiteUrl(getRegionalProductPath(region))
  const productOgImage = 'https://d1tg5gdu842vo5.cloudfront.net/site-assets/images/hero-images/child_1_hero_desktop.webp'

  // Images for Google product rich results, sourced from live product data so they
  // always track the current catalogue image (rather than a hard-coded URL that
  // would 404 if the product image is re-uploaded). Prefers the selected
  // colourway's hero foreground + gallery; falls back to the first colourway, then
  // a static lifestyle hero. Google accepts an array and picks the best per surface.
  const productSchemaImages = useMemo(() => {
    const STATIC_FALLBACK = 'https://d1tg5gdu842vo5.cloudfront.net/site-assets/images/hero-images/child_1_hero_desktop.webp'
    const colourways = options.find(option => option.required)?.options || []
    const source = selectedColourwayProduct || colourways[0] || null
    const hero = (source as { heroImage?: { foregroundUrl?: string } | null } | null)?.heroImage
    const galleryImages = (source as { images?: Array<{ url?: string }> } | null)?.images || []
    const urls = [hero?.foregroundUrl, ...galleryImages.map(image => image?.url)]
      .filter((url): url is string => typeof url === 'string' && url.startsWith('http'))
    const unique = Array.from(new Set(urls))
    return unique.length ? unique : [STATIC_FALLBACK]
  }, [options, selectedColourwayProduct])

  // Offer fields derived from live data so the structured data matches what the
  // shopper sees (Google disapproves merchant listings whose markup price/currency
  // disagree with the page). priceValidUntil is required-ish for rich results.
  const offerPrice = currentPrice > 0
    ? currentPrice
    : (selectedColourwayProduct?.price?.unit_amount ?? 39)
  const offerCurrency = currencyCode || 'GBP'
  const offerSku = selectedColourwayProduct?._id || DEFAULT_PRODUCT_ID
  const offerAvailability = isPreorder
    ? 'https://schema.org/PreOrder'
    : 'https://schema.org/InStock'
  const priceValidUntil = `${new Date().getFullYear()}-12-31`
  const catalogueReviewCount = product?.totalCatalogueReviews ?? 0
  const catalogueRating = product?.averageCatalogueRating

  const productJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: 'Kibu Wired Headphones',
    description: 'DIY headphone kit for children — kids build their own wired headphones from sustainable bioplastic. Buildable, repairable and recyclable. Designed for safe listening and hands-on STEM learning. Suitable for ages 5–14.',
    brand: { '@type': 'Brand', name: 'Kibu' },
    audience: { '@type': 'PeopleAudience', suggestedAge: '5–14', suggestedGender: 'Unisex' },
    material: 'Bioplastic (plant-based, corn-derived)',
    award: 'Gold — Better Future Awards, Circular Economy',
    sku: offerSku,
    mpn: DEFAULT_PRODUCT_ID,
    url: canonicalUrl,
    image: productSchemaImages,
    // Only emit aggregateRating when there are genuine approved reviews — never
    // fabricate ratings (against Google policy and risks a manual action).
    ...(catalogueReviewCount > 0 && typeof catalogueRating === 'number'
      ? {
        aggregateRating: {
          '@type': 'AggregateRating',
          ratingValue: capDisplayRating(catalogueRating),
          reviewCount: catalogueReviewCount,
          bestRating: 5,
          worstRating: 1,
        },
      }
      : {}),
    offers: {
      '@type': 'Offer',
      priceCurrency: offerCurrency,
      price: offerPrice.toFixed(2),
      priceValidUntil,
      availability: offerAvailability,
      itemCondition: 'https://schema.org/NewCondition',
      url: canonicalUrl,
      seller: { '@type': 'Organization', name: 'Kibu', url: 'https://kibu.family/' },
    },
  }

  if (productLoadError) {
    return <>
      <Helmet><title>{searchMetadata.product.title}</title></Helmet>
      <ProductLoadFailure message={productLoadError} onRetry={() => {
        setIsLoading(true)
        setProductLoadError(null)
        setProductLoadAttempt(attempt => attempt + 1)
      }} />
    </>
  }

  if (!isLoading && product && !hasListedColourways) {
    return <>
      <Helmet><title>{searchMetadata.product.title}</title></Helmet>
      <ProductLoadFailure title={product.productTitle || product.productName}
        message="No colourways are available to buy right now. Please check back soon."
        onRetry={() => {
          setIsLoading(true)
          setProductLoadAttempt(attempt => attempt + 1)
        }} />
    </>
  }

  return (
    <>
      <Helmet>
        <title>{searchMetadata.product.title}</title>
        <meta name="description" content={searchMetadata.product.description} />
        <link rel="canonical" href={canonicalUrl} />
        {SEO_REGIONS.map(({ code, hreflang }) => (
          <link
            key={code}
            rel="alternate"
            hrefLang={hreflang}
            href={toAbsoluteSiteUrl(getRegionalProductPath(code))}
          />
        ))}
        <link
          rel="alternate"
          hrefLang="x-default"
          href={toAbsoluteSiteUrl(getRegionalProductPath('gb'))}
        />
        <meta property="og:type" content="product" />
        <meta property="og:title" content={searchMetadata.product.title} />
        <meta property="og:description" content={searchMetadata.product.description} />
        <meta property="og:image" content={productOgImage} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:site_name" content="Kibu" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={searchMetadata.product.title} />
        <meta name="twitter:description" content={searchMetadata.product.description} />
        <meta name="twitter:image" content={productOgImage} />
        {hasReadyColourway && <script type="application/ld+json">{JSON.stringify(productJsonLd)}</script>}
      </Helmet>
      <div
        className="contents"
        data-prerender-ready={!isLoading && hasReadyColourway && product?.productTitle ? 'true' : undefined}
      >
        <ProductPageShell
          navBackgroundClassName={currentTheme?.navBackgroundColor || 'bg-white'}
          navBorderClassName={currentTheme?.borderColor || 'border-grey'}
          navClassName={`transition-all ease-hypereaseout ${customColoursOpen ? 'duration-[2400ms] opacity-70 blur-md' : 'duration-[2000ms] opacity-100 blur-none'}`}
          backgroundClassName={currentTheme?.backgroundColor || ''}
          mainContainerClassName={`${customColoursOpen ? 'scale-[0.95] blur-sm du duration-[1400ms] ease-hypereaseout' : 'scale-[1.00] duration-1000 ease-hypereaseout'}`}
          mainContainerStyle={{ marginTop: '1em' }}
          navContent={(
            <div className='standard_container flex !flex-row justify-between items-center md:pl-8 md:pr-8'>
              <div className="flex items-center gap-2 md:gap-4 md:ml-16">
                {isCurrencyLoading || isLoading || (!hasReadyColourway && !allListedColourwaysUnavailable) ? (
                  <div className='price text-orange text-2xl md:text-3xl md:w-20 w-12 flex items-center justify-center'>
                    <LoadingRing size="sm" />
                  </div>
                ) : (
                  <h3 className={`price text-2xl md:text-3xl md:w-20 w-12 ${currentTheme?.textColor || 'text-orange'}`}>
                    {formatPriceWithSymbol(currencySymbol, displayPrice)}
                  </h3>
                )}
                <SelectedItems
                  colourWay={options[0]?.options.find(cw => cw._id === selectedColorWay) || null}
                  selectedAddons={getSelectedAddonsAsProduct(addonsSelectedItemIds.flatMap(item =>
                    Object.values(item).filter((value): value is string => value !== null)
                  ))}
                  maxItems={3}
                />
              </div>
              <button
                className={`flex items-center bg-origin-border p-2 md:pl-4 md:mr-16 md:pr-4 ${stickyButtonVariantClassName} justify-center w-min-content rounded-md font-headline md:text-lg leading-none
						transition-all ease-kibu duration-200 items-center cursor-pointer border border-2 border-transparent
						hover:rounded-lg hover:brightness-105 hover:border-black/10 hover:scale-[1.02]
						${(isLoading || isAddingToBasket) ? 'opacity-50 cursor-not-allowed hover:scale-100 hover:brightness-100' : ''}`}
                onClick={() => handleAddToBasket()}
                disabled={isLoading || isAddingToBasket || !hasReadyColourway}
              >
                {isLoading || isAddingToBasket ? 'Loading...' : !hasReadyColourway ? 'Currently unavailable' : 'Add to Basket'}
              </button>
            </div>
          )}
          mainContent={(
            <>
              <div className="w-full py-4 md:hidden">
                {isLoading || (!hasReadyColourway && !allListedColourwaysUnavailable) ? (
                  <Skeleton className="h-12 w-3/4" />
                ) : (
                  // Visual copy only: ProductInfo keeps the page's single <h1> (screen-reader visible on mobile),
                  // because a second <h1> fails the prerender's one-heading check.
                  <p aria-hidden="true" className={`font-headline antialiased text-left leading-none text-4xl ${currentTheme?.primaryTextColor || ''}`}>
                    {product?.productTitle?.trim() || 'Kibu Wired Headphones'}
                  </p>
                )}
              </div>
              <ProductImageSection
                isLoading={isLoading || (selectedColorWay === null && Boolean(rankedAvailableColourway))}
                images={slideshowImages}
                activeProductImage={activeProductImage}
                showEditColors={selectedColorWay === globalCustomColourProduct?._id}
                onEditColors={() => setCustomColoursOpen(true)}
                onAllImagesLoaded={() => setImagesLoaded(true)}
                estimatedDispatchDays={dispatchEstimate}
                isPreorder={isPreorder}
                specialLogo={currentTheme?.specialLogo}
                specialLogoSize={currentTheme?.specialLogoSize}
                customHero={customHeroData}
              />
              <div id="product-info" className="information-container w-full md:max-w-[600px] pt-2 md:pt-6 flex flex-col gap-0">
                <ProductInfo
                  hideTitleOnMobile
                  _product={productsData[0]}
                  currentPrice={displayPrice}
                  currency={currencySymbol}
                  className="mb-6"
                  isLoading={isLoading || (!hasReadyColourway && !allListedColourwaysUnavailable)}
                  primaryTextColor={currentTheme?.primaryTextColor}
                  accentColor={currentTheme?.accentColor}
                  isPreorder={isPreorder}
                  preorderMessage={preorderMessage}
                  preorderNoticeVariant={currentTheme?.isDarkBackground ? 'dark' : 'light'}
                  holidayInfo={holidayInfo}
                  descriptionOverride={currentTheme?.descriptionOverride}
                  descriptionLink={currentTheme?.descriptionLink}
                  ratingOverride={reviewSummary?.average}
                  reviewCountOverride={reviewSummary?.total}
                  reviewProductId={productRequestId}
                  reviewRegion={region}
                />

                {allListedColourwaysUnavailable && !isLoading && <p role="status" className="mb-5 rounded-lg border border-current/20 p-4 text-sm">No colourways are available to buy right now. You can still browse them below and check their availability.</p>}
                <ProductAddonSelector
                  isLoading={isLoading}
                  options={options}
                  colourWays={visibleColourWays}
                  selectedColorWay={selectedColorWay}
                  currencySymbol={currencySymbol}
                  discountCode={discountCode}
                  stockById={stockById}
                  basketRequestedCounts={basketRequestedCounts}
                  getActiveId={getActiveId}
                  isAddonFree={isAddonFree}
                  handleConfiguratorChoice={handleConfiguratorChoice}
                  setSelectedColorWay={setSelectedColorWay}
                  setIsInitialLoad={setIsInitialLoad}
                  setCustomColoursOpen={setCustomColoursOpen}
                  setMoreColoursData={setMoreColoursData}
                  setShowMoreColoursModal={setShowMoreColoursModal}
                  handleAddonSelect={handleAddonSelect}
                  inputChangeHandlers={inputChangeHandlers}
                  primaryTextColor={currentTheme?.primaryTextColor}
                  secondaryTextColor={currentTheme?.textColor}
                  priceColor={currentTheme?.accentColor}
                  onBlockedSelect={handleBlockedAddonSelect}
                />

                <ProductActionButton
                  isLoading={isLoading || isAddingToBasket || (!hasReadyColourway && !allListedColourwaysUnavailable)}
                  buttonLabel={allListedColourwaysUnavailable ? 'Currently unavailable' : undefined}
                  isDisabled={!selectedPurchasableColourway}
                  onAddToBasket={handleAddToBasket}
                  currencySymbol={currencySymbol}
                  freeDeliveryThreshold={freeDeliveryThreshold ?? 5000}
                  countryDetails={countryDetails}
                  formattedUpdateDate={formattedUpdateDate}
                  estimatedDispatchDays={dispatchEstimate}
                  buttonVariant={currentTheme?.buttonVariant || null}
                  primaryTextColor={currentTheme?.primaryTextColor}
                  iconColor={currentTheme?.primaryTextColor}
                />
                <InlineReviews productId={productRequestId} region={region} className="mt-6" />
                {/* {product?.productDescription?.mobile && (
              <div className={`md:hidden ${currentTheme?.primaryTextColor || ''}`}>
                {product.productDescription.mobile}
              </div>
            )} */}
              </div>
            </>
          )}
        >
          <ProductBottomSections
            galleryImages={galleryImages}
            productId={productRequestId}
            region={region}
          />
          <ProductModals
            showConfiguratorModal={showConfiguratorModal}
            showMoreColoursModal={showMoreColoursModal}
            moreColoursData={moreColoursData}
            selectedColorWay={selectedColorWay}
            navHeight={navHeight}
            handleConfiguratorChoice={handleConfiguratorChoice}
            setShowMoreColoursModal={setShowMoreColoursModal}
            setSelectedColorWay={setSelectedColorWay}
          />
        </ProductPageShell>
      </div>
    </>
  )
}

export default ProductPage
